import { View } from "@novorender/api";
import { quat, ReadonlyVec3, vec3 } from "gl-matrix";

import { isRealVec } from "utils/misc";

export type SurfaceProjectionSize = {
    width: number;
    height: number;
};

export type SurfaceProjection = {
    normal: vec3;
    transform: string;
    isTilted: boolean;
};

/**
 * Computes a CSS/SVG transform string to project a surface icon so it matches the surface normal at a given screen position.
 * Returns a string like 'rotate3d(x, y, z, angle)' or an empty string if no rotation is needed.
 */
export function getSurfaceProjectionTransform(
    normalVS: ReadonlyVec3,
    x: number,
    y: number,
    size: SurfaceProjectionSize,
    view: View,
): string {
    return getSurfaceProjection(normalVS, x, y, size, view)?.transform ?? "";
}

export function getSurfaceProjection(
    normalVS: ReadonlyVec3 | undefined,
    x: number,
    y: number,
    size: SurfaceProjectionSize,
    view: View,
): SurfaceProjection | undefined {
    if (!normalVS || normalVS.length !== 3 || !isRealVec(normalVS)) {
        return undefined;
    }

    const { camera } = view.renderState;
    const normal = vec3.clone(normalVS);
    let transform = "";
    let isTilted = false;

    if (normal[2] < 0.98) {
        isTilted = true;
        const angleX = (y / size.height - 0.5) * camera.fov;
        const angleY = ((x - size.width * 0.5) / size.height) * camera.fov;
        vec3.transformQuat(normal, normal, quat.fromEuler(quat.create(), angleX, angleY, 0));
        if (normal[2] < 1) {
            const rot = vec3.cross(vec3.create(), normal, vec3.fromValues(0, 0, 1));
            vec3.normalize(rot, rot);
            const angle = (Math.acos(normal[2]) * 180) / Math.PI;
            transform = `rotate3d(${rot[0]},${-rot[1]},${rot[2]},${angle}deg)`;
        }
    }

    return { normal, transform, isTilted };
}
